import { useState } from 'react'
import { BarraPaginacion } from '../componentes/BarraPaginacion'
import { EstadoError } from '../componentes/EstadoError'
import { contarPaginas, TAMANO_PAGINA } from '../componentes/paginacion'
import { EstadoVacio } from '../componentes/EstadoVacio'
import { CabeceraModal, CabeceraPagina, CLASE_BOTON, CLASE_BOTON_PRIMARIO, CLASE_INPUT, Insignia } from '../componentes/formulario'
import { fechaCorta } from '../componentes/formato'
import { IconoAnadir, IconoBuscar } from '../componentes/iconos'
import { Modal } from '../componentes/Modal'
import { PerfilCliente } from '../componentes/PerfilCliente'
import { SkeletonList } from '../componentes/Skeleton'
import type { ClienteListado } from '../api/modelos'
import { useCrearCliente, useListaClientes } from '../hooks/useGestionClientes'
import { FormCliente } from './clientes/FormCliente'
import { FichaCliente } from './clientes/FichaCliente'
import { cuerpoAlta, VACIO } from './clientes/formCliente'
import { DIAS, SEMANAS } from './agendamiento/tabla'

function rotuloRuta(ruta?: string | null) {
  return DIAS.find((d) => d.clave === ruta)?.etiqueta ?? '—'
}
function rotuloSemana(semana?: string | null) {
  return SEMANAS.find((s) => s.clave === semana)?.etiqueta ?? '—'
}

function EstadoCliente({ c }: { c: ClienteListado }) {
  if (c.activo === false) {
    return <Insignia tono="atencion">{c.bajaHasta ? `De baja hasta ${fechaCorta(c.bajaHasta)}` : 'De baja'}</Insignia>
  }
  return <Insignia tono="positivo">Activo</Insignia>
}

/**
 * Clientes (RF-40): lista con búsqueda, alta y ficha editable con etiquetas,
 * coordenadas, precios pactados (RF-42) y baja o reactivación. Sin Holded: los
 * clientes nacen en el módulo.
 */
export function Clientes() {
  const [q, setQ] = useState('')
  const [page, setPage] = useState(1)
  const [alta, setAlta] = useState(false)
  const [abierto, setAbierto] = useState<ClienteListado | null>(null)
  const [perfil, setPerfil] = useState<string | null>(null)
  const [conBajas, setConBajas] = useState(false)
  const lista = useListaClientes(q, page, TAMANO_PAGINA, conBajas)
  const crear = useCrearCliente()
  const items = lista.data?.items ?? []
  const paginas = contarPaginas(lista.data?.total ?? 0)

  return (
    <div className="flex flex-col gap-4">
      <CabeceraPagina
        titulo="Clientes"
        descripcion="Alta y edición de clientes con su ruta, su semana y su ubicación. Abre la ficha para fijar precios pactados o darlos de baja."
        acciones={
          <button type="button" className={CLASE_BOTON_PRIMARIO} onClick={() => { crear.reset(); setAlta(true) }}>
            <IconoAnadir aria-hidden="true" className="size-4" />
            Nuevo cliente
          </button>
        }
      />

      <div className="flex flex-wrap items-center gap-4">
        <label className="relative block w-full max-w-md">
          <span className="sr-only">Buscar cliente</span>
          <IconoBuscar aria-hidden="true" className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-tinta-apagada" />
          <input
            type="search"
            value={q}
            onChange={(e) => { setQ(e.target.value); setPage(1) }}
            placeholder="Buscar por nombre o móvil"
            className={`${CLASE_INPUT} pl-9`}
          />
        </label>
        <label className="flex items-center gap-2 text-sm text-tinta-2">
          <input type="checkbox" checked={conBajas} onChange={(e) => { setConBajas(e.target.checked); setPage(1) }} />
          Mostrar también las bajas
        </label>
      </div>

      {lista.isPending ? (
        <SkeletonList />
      ) : lista.isError ? (
        <EstadoError error={lista.error} onReintentar={() => void lista.refetch()} />
      ) : items.length === 0 ? (
        <EstadoVacio
          titulo={q ? 'Ningún cliente coincide con la búsqueda' : 'Todavía no hay clientes'}
          descripcion={q ? 'Prueba con otra parte del nombre o del móvil.' : 'Da de alta el primero con «Nuevo cliente».'}
        />
      ) : (
        <div className="overflow-x-auto rounded-xl border border-borde bg-superficie">
          <table className="w-full text-left text-sm">
            <thead className="border-b border-borde text-xs uppercase tracking-wide text-tinta-apagada">
              <tr>
                <th className="px-4 py-2 font-semibold">Nombre</th>
                <th className="px-4 py-2 font-semibold">Móvil</th>
                <th className="px-4 py-2 font-semibold">Ciudad</th>
                <th className="px-4 py-2 font-semibold">Ruta</th>
                <th className="px-4 py-2 font-semibold">Semana</th>
                <th className="px-4 py-2 font-semibold">Estado</th>
                <th className="px-4 py-2"><span className="sr-only">Acciones</span></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-borde-suave">
              {items.map((c) => (
                <tr key={c.id}>
                  <td className="px-4 py-2 font-medium text-tinta">{c.nombre}</td>
                  <td className="px-4 py-2 tabular-nums text-tinta-2">{c.movil || '—'}</td>
                  <td className="px-4 py-2 text-tinta-2">{c.ciudad || '—'}</td>
                  <td className="px-4 py-2 text-tinta-2">{rotuloRuta(c.ruta)}</td>
                  <td className="px-4 py-2 text-tinta-2">{rotuloSemana(c.semana)}</td>
                  <td className="px-4 py-2"><EstadoCliente c={c} /></td>
                  <td className="px-4 py-2 text-right">
                    <div className="flex justify-end gap-2">
                      <button type="button" className={CLASE_BOTON} onClick={() => setPerfil(c.id)} aria-label={`Ver el perfil de ${c.nombre}`}>
                        Perfil
                      </button>
                      <button type="button" className={CLASE_BOTON} onClick={() => setAbierto(c)} aria-label={`Abrir la ficha de ${c.nombre}`}>
                        Ficha
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <BarraPaginacion pagina={page} paginas={paginas} onCambiar={setPage} />

      {alta ? (
        <Modal titulo="Nuevo cliente" onCerrar={() => setAlta(false)} anchoMax="max-w-3xl">
          <CabeceraModal titulo="Nuevo cliente" onCerrar={() => setAlta(false)} />
          <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
            <FormCliente
              inicial={VACIO}
              rotuloGuardar="Dar de alta"
              guardando={crear.isPending}
              error={crear.error}
              onCancelar={() => setAlta(false)}
              onGuardar={(v) => crear.mutate(cuerpoAlta(v), { onSuccess: () => setAlta(false) })}
            />
          </div>
        </Modal>
      ) : null}

      {abierto ? <FichaCliente id={abierto.id} nombre={abierto.nombre} onCerrar={() => setAbierto(null)} /> : null}
      <PerfilCliente contactId={perfil} onCerrar={() => setPerfil(null)} />
    </div>
  )
}
